/**
 * The consolidated weekly order view: a scrollable column of expandable day
 * cards (Mon–Fri) plus a right rail (month calendar + order deadline + cart).
 * The week stepper that drives `anchorKey` lives in the page header (see
 * `SchoolOrdersView`); this view receives the selected week as a prop.
 *
 * Below `lg` there is no rail, so the column ends in an "Options" menu holding
 * the quick actions that can't fit the page's controls row (PROD-299), and a
 * cart drawer is pinned beneath it.
 *
 * Submitted batches for the visible week (which may straddle two months) are
 * fetched per overlapped month and merged into the already-ordered lookups the
 * day cards read.
 */

import { cn } from '@ordo/ui/cn'
import { useQueries } from '@tanstack/react-query'
import { Clock } from 'lucide-react'
import { type ReactElement, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useOutstandingDaysQuery } from '@/api/cafeteria'
import { useFieldTripElectionsQuery } from '@/api/field-trips'
import { fetchLocationActiveMenus, locationActiveMenusQueryKey, MENUS_STALE_TIME_MS } from '@/api/orders'
import {
  type AdminSchool,
  fetchSchoolForecastBatchesForMonth,
  type SchoolForecastBatchRow,
  serviceDateIso,
  useSchoolForecastBatchesForMonthQuery,
  useSchoolServiceStatusForMonthQuery,
} from '@/api/school-orders'
import { OrderingPausedBanner } from '@/components/cafeteria/ordering-paused-banner'
import { orderableMenusFor } from '@/lib/school-orders/active-menus'
import { aggregateAlreadyOrdered, buildAlreadyOrdered } from '@/lib/school-orders/already-ordered'
import { dateFromKey, todayKey, weekOf } from '@/lib/school-orders/calendar'
import { totalQty } from '@/lib/school-orders/cart'
import { bandAudienceFold } from '@/lib/school-orders/columns'
import { dayCtaState, primaryOrderNowIndex } from '@/lib/school-orders/day-cta'
import {
  type DeadlineStatus,
  orderClosedReason,
  schoolDeadlinePolicy,
  weekDeadlineStatus,
} from '@/lib/school-orders/deadline'
import { useIsMobile } from '@/lib/use-is-mobile'
import { selectLinesFor, useSchoolCart } from '@/stores/school-cart'

import { DayCard } from './day-card'
import { MobileCartBar } from './mobile-cart-bar'
import { RightRail } from './right-rail'
import { SchoolOrderActions } from './school-order-actions'

function monthOf(dateKey: string): { year: number; month: number } {
  const d = dateFromKey(dateKey)
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 }
}

export function WeekOrdersView({
  school,
  anchorKey,
  onAnchorChange,
  readOnly = false,
  lookahead = false,
  onScrolledChange,
  schools,
  onSelectSchool,
}: {
  school: AdminSchool
  /** Monday key of the selected week (driven by the header week stepper). */
  anchorKey: string
  onAnchorChange: (mondayKey: string) => void
  readOnly?: boolean
  /**
   * Cafeteria "Orders lookup": the same week view, VIEW-ONLY, with every
   * quantity the school + parent COMBINED total. No cart/ordering chrome;
   * days expand in place on every breakpoint (there is no cafeteria
   * day-page route to navigate to).
   */
  lookahead?: boolean
  /** Fires when the day list starts/stops being scrolled away from the top. */
  onScrolledChange?: (scrolled: boolean) => void
  /** All the admin's schools — feeds the day panel's location tab strip. */
  schools?: AdminSchool[]
  /** Select another campus from the day panel's location tab strip. */
  onSelectSchool?: (locationId: string) => void
}): ReactElement {
  const weekKeys = useMemo(() => weekOf(anchorKey), [anchorKey])

  const monthA = monthOf(weekKeys[0]!)
  const monthB = monthOf(weekKeys[weekKeys.length - 1]!)
  const batchesA = useSchoolForecastBatchesForMonthQuery({ schoolLocationId: school.id, ...monthA })
  const batchesB = useSchoolForecastBatchesForMonthQuery({ schoolLocationId: school.id, ...monthB })

  const rows = useMemo<SchoolForecastBatchRow[]>(
    () => [
      ...(batchesA.data?.schoolForecastBatchesForMonth ?? []),
      ...(monthA.month !== monthB.month ? (batchesB.data?.schoolForecastBatchesForMonth ?? []) : []),
    ],
    [batchesA.data, batchesB.data, monthA.month, monthB.month],
  )
  // Parent-demand overlay rows ride the same month payloads — assembled
  // with the same dual-month merge as `rows` so the "+X parent orders"
  // legend covers cross-month weeks.
  const familyRows = useMemo(
    () => [
      ...(batchesA.data?.familyOrders ?? []),
      ...(monthA.month !== monthB.month ? (batchesB.data?.familyOrders ?? []) : []),
    ],
    [batchesA.data, batchesB.data, monthA.month, monthB.month],
  )
  const alreadyOrdered = useMemo(
    () =>
      buildAlreadyOrdered(rows, {
        wholeSchool: school.orderGroupBy === 'NONE',
        familyOrders: familyRows,
        // Lookahead shows one COMBINED number per cell instead of the
        // school number + "+X parent orders" legend.
        combineFamilyIntoTotals: lookahead,
        // Fold campus-tier audiences into the locked band columns the
        // sheet renders (orders placed before the band cutover).
        foldTierAudience: bandAudienceFold(school),
      }),
    [rows, familyRows, school, lookahead],
  )
  // Sibling campuses' month payloads — the COLLAPSED week list totals across
  // the whole district (Sheila, 2026-08-24: a collapsed card showing whichever
  // campus the slider last touched has no use case and read as the district
  // total). Same query keys the sibling campus's own view uses → shared cache.
  const siblingSchools = useMemo(() => (schools ?? []).filter((s) => s.id !== school.id), [schools, school.id])
  const monthPairs = useMemo(
    () => (monthA.month !== monthB.month ? [monthA, monthB] : [monthA]),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- monthOf returns a fresh object per render; year+month are its whole identity
    [monthA.month, monthA.year, monthB.month, monthB.year],
  )
  const siblingBatches = useQueries({
    queries: siblingSchools.flatMap((sibling) =>
      monthPairs.map((mo) => ({
        queryKey: [
          'school-orders',
          'forecast-batches',
          { schoolLocationId: sibling.id, year: mo.year, month: mo.month },
        ],
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          fetchSchoolForecastBatchesForMonth({ schoolLocationId: sibling.id, year: mo.year, month: mo.month }, signal),
      })),
    ),
  })
  const collapsedAlreadyOrdered = useMemo(() => {
    if (siblingSchools.length === 0) {
      return alreadyOrdered
    }
    const perSibling = siblingSchools.map((sibling, index) => {
      const payloads = monthPairs.map((_, monthIndex) => siblingBatches[index * monthPairs.length + monthIndex]?.data)
      return buildAlreadyOrdered(
        payloads.flatMap((payload) => payload?.schoolForecastBatchesForMonth ?? []),
        {
          wholeSchool: sibling.orderGroupBy === 'NONE',
          familyOrders: payloads.flatMap((payload) => payload?.familyOrders ?? []),
          combineFamilyIntoTotals: lookahead,
          foldTierAudience: bandAudienceFold(sibling),
        },
      )
    })
    return aggregateAlreadyOrdered([alreadyOrdered, ...perSibling])
  }, [alreadyOrdered, siblingSchools, siblingBatches, monthPairs, lookahead])
  // Calendar markers: every day with at least one submitted batch. VOIDED
  // batches are excluded server-side; the filter is defensive for stale
  // caches so a just-cancelled day can't keep its dot (#1150 mirror).
  const orderedDates = useMemo(
    () => new Set(rows.filter((b) => b.status !== 'VOIDED').map((b) => b.serviceDate.slice(0, 10))),
    [rows],
  )

  // History-mode badges (PROD-1123 v2): per-day service-completion flags,
  // same dual-month merge as the batches. Not fetched on the cafeteria
  // lookahead surface — the endpoint gates on SCHOOL_ADMIN and history mode
  // is a school surface.
  const statusA = useSchoolServiceStatusForMonthQuery({ schoolLocationId: lookahead ? '' : school.id, ...monthA })
  const statusB = useSchoolServiceStatusForMonthQuery({ schoolLocationId: lookahead ? '' : school.id, ...monthB })
  const serviceCompleteByDay = useMemo(() => {
    const map = new Map<string, boolean>()
    for (const day of [
      ...(statusA.data?.days ?? []),
      ...(monthA.month !== monthB.month ? (statusB.data?.days ?? []) : []),
    ]) {
      map.set(day.serviceDate, day.serviceComplete)
    }
    return map
  }, [statusA.data, statusB.data, monthA.month, monthB.month])

  // Days the school marked NO_SERVICE (PROD-1228), merged across the two
  // months the week can straddle. Same payload as the History badges, so this
  // costs no extra fetch. The submit gate rejects these days server-side; the
  // calendar strikes them through so nobody is invited to try.
  const noServiceDates = useMemo(() => {
    return new Set<string>([
      ...(statusA.data?.noServiceDates ?? []),
      ...(monthA.month !== monthB.month ? (statusB.data?.noServiceDates ?? []) : []),
    ])
  }, [statusA.data, statusB.data, monthA.month, monthB.month])

  // History-mode activity beyond school orders: committed parent demand per
  // day. A parent-order-only campus places no school batches, so gating
  // History mode on `dayQty` alone would leave its past days inert and the
  // service-details page unreachable (PROD-1123's own audience).
  const familyQtyByDay = useMemo(() => {
    const map = new Map<string, number>()
    for (const row of familyRows) {
      const dateKey = row.serviceDate.slice(0, 10)
      map.set(dateKey, (map.get(dateKey) ?? 0) + row.quantity)
    }
    return map
  }, [familyRows])

  // Per-day CTA states for the card headers ("Show details" / "Order now" /
  // inert). Menu presence reuses the exact per-day query CollapsedSummary and
  // DayOrderPanel already fire (same key → deduped, no extra network). No
  // keepPreviousData on these observers: a week change must not leak the
  // previous week's menus into the new week's pill states.
  const hasMenuByDay = useQueries({
    queries: weekKeys.map((dateKey) => ({
      queryKey: locationActiveMenusQueryKey({
        locationId: school.id,
        activeOn: serviceDateIso(dateKey),
        withAllergens: true,
      }),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchLocationActiveMenus(
          { locationId: school.id, activeOn: serviceDateIso(dateKey), withAllergens: true },
          signal,
        ),
      staleTime: MENUS_STALE_TIME_MS,
    })),
    combine: (results) =>
      results.map((r) => (r.data === undefined ? null : orderableMenusFor(r.data).mealtimes.length > 0)),
  })
  const cartLines = useSchoolCart(selectLinesFor(school.id))
  // A read-only surface offers no cart and no checkout: it can never add a
  // line (FACTORY-554). Unless another campus's cart holds lines — the cart
  // spans every campus the admin manages, and hiding real pending lines is
  // how items got lost before (c20d4f27 follow-up).
  const linesByLocation = useSchoolCart((s) => s.linesByLocation)
  const crossCampusCartEmpty = (schools && schools.length > 0 ? schools : [school]).every(
    (s) => totalQty(linesByLocation[s.id] ?? []) === 0,
  )
  const policy = schoolDeadlinePolicy(school)
  // Strictly past by the viewer clock OR service already completed — either
  // flips the day to History mode (badge + "View service details").
  const todayK = todayKey()
  // Past, or past its order deadline. One answer for the day pills and the
  // cart, so they can never disagree about whether the week is orderable.
  const closedByDay = weekKeys.map((dateKey) => orderClosedReason(dateKey, policy, todayK) !== null)
  // A week with no orderable day left offers no cart either: "Your cart is
  // empty" under "Ordering closed for this week" invites an action that can't
  // apply (PROD-1472). Same cross-campus guard as read-only, so lines placed
  // for another week or campus stay reachable.
  const hideCart = lookahead || ((readOnly || closedByDay.every(Boolean)) && crossCampusCartEmpty)
  // The deadline countdown / closed state, surfaced at the TOP of the
  // collapsed list (below the week selector, above Monday) — the same
  // indicator as the rail calendar footer, promoted so it's in the user's
  // face without opening a day. Null `daysRemaining` = no policy → hide.
  const weekDeadline = weekDeadlineStatus(policy, weekKeys[0] ?? '')
  // Ordering pause (outstanding past service days at/over threshold) surfaces
  // HERE, not at checkout — the admin must learn about the block before
  // building a cart. The server-side submit gate stays the enforcement.
  const outstandingDays = useOutstandingDaysQuery({ schoolLocationId: school.id })
  // Parent-order field trip elections → a pill on the day card, so an admin
  // can see at a glance which days they already opened to parents.
  const electionsQuery = useFieldTripElectionsQuery(school.id, { enabled: !lookahead && !readOnly })
  const parentFieldTripByDay = useMemo(() => {
    const out = new Map<string, { fieldTripOnly: boolean }>()
    for (const e of electionsQuery.data ?? []) {
      const prev = out.get(e.serviceDate)
      out.set(e.serviceDate, { fieldTripOnly: (prev?.fieldTripOnly ?? false) || e.fieldTripOnly })
    }
    return out
  }, [electionsQuery.data])
  const orderingPaused = outstandingDays.data?.blocked === true
  const dayCta = weekKeys.map((dateKey, i) =>
    dayCtaState({
      hasMenu: hasMenuByDay[i] ?? null,
      hasOrders: collapsedAlreadyOrdered.dayQty(dateKey) > 0,
      closed: closedByDay[i] === true,
      readOnly: readOnly || lookahead,
      history: !lookahead && (dateKey < todayK || serviceCompleteByDay.get(dateKey) === true),
      // Parent orders or a completed cafeteria service (register-only days)
      // open History mode too — school batches are not the only activity.
      historyActivity: (familyQtyByDay.get(dateKey) ?? 0) > 0 || serviceCompleteByDay.get(dateKey) === true,
      // Read-only surfaces (a PARENT-only campus above all) keep every day
      // with parent orders reviewable, today and future included: parent
      // demand is the only thing such a campus has to show.
      parentOrders: (familyQtyByDay.get(dateKey) ?? 0) > 0,
      // A day the school marked NO_SERVICE never offers "Order now" — the
      // submit gate refuses it (PROD-1228).
      noService: noServiceDates.has(dateKey),
    }),
  )
  const primaryIndex = primaryOrderNowIndex(
    dayCta,
    cartLines.length === 0,
    hasMenuByDay.every((v) => v !== null),
  )

  // Desktop: expanding a day turns it into an in-place "page" that fills the
  // column height (hiding the rest); the rail stays put. Mobile: there is no
  // in-place expand — tapping a day navigates to the full-page day view.
  const isMobile = useIsMobile()
  const dayHref = (dk: string): string => `/orders/school/day?locationId=${encodeURIComponent(school.id)}&date=${dk}`
  const serviceHref = (dk: string): string =>
    `/orders/school/service?locationId=${encodeURIComponent(school.id)}&date=${dk}`
  // An expanded day stays open until its own "Hide details" control collapses
  // it — there is deliberately no click-outside dismissal, so stray clicks in
  // the rail, the calendar, or dead space cannot discard in-progress entry.
  const [expandedDate, setExpandedDate] = useState<string | null>(null)
  // Changing the week (header stepper or rail calendar) collapses any open day.
  useEffect(() => {
    setExpandedDate(null)
  }, [anchorKey])

  // Pin the rail to the collapsed day-card column's content height (cards +
  // gaps) so the cart is a FIXED height — the same whether a day is expanded or
  // not. We can only measure the column in the collapsed view (the expanded
  // view replaces it with a single card), so we retain the last measurement
  // and keep applying it while a day is expanded.
  const dayListRef = useRef<HTMLDivElement>(null)
  const [dayListHeight, setDayListHeight] = useState<number | null>(null)
  useLayoutEffect(() => {
    const el = dayListRef.current
    if (!el) {
      return // expanded view: keep the last collapsed measurement
    }
    const update = (): void => setDayListHeight(el.offsetHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [expandedDate, weekKeys, alreadyOrdered])

  // Only the page-level "Orders" header (with the week stepper) stays fixed;
  // the day cards scroll. The rail is a sibling of the left column. On mobile
  // (no rail) a bottom cart bar is pinned beneath the scrolling list.
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 gap-3">
        {expandedDate && (!isMobile || lookahead) ? (
          // Expanded: a single day fills the rest; only that day's table scrolls.
          // `min-w-0` lets this flex column shrink below the table's intrinsic
          // width so the table scrolls internally instead of pushing the rail.
          <div className="flex min-h-0 min-w-0 flex-1 flex-col pb-4 pr-1">
            <DayCard
              key={expandedDate}
              ordersPending={batchesA.isPending || (monthA.month !== monthB.month && batchesB.isPending)}
              school={school}
              dateKey={expandedDate}
              alreadyOrdered={alreadyOrdered}
              readOnly={readOnly || lookahead}
              lookahead={lookahead}
              parentFieldTrip={parentFieldTripByDay.get(expandedDate) ?? null}
              serviceDetailsHref={lookahead ? undefined : serviceHref(expandedDate)}
              serviceComplete={serviceCompleteByDay.get(expandedDate) === true}
              noService={noServiceDates.has(expandedDate)}
              cta={dayCta[weekKeys.indexOf(expandedDate)] ?? 'details'}
              schools={schools}
              onSelectSchool={onSelectSchool}
              open
              onOpenChange={(o) => {
                if (!o) {
                  setExpandedDate(null)
                }
              }}
            />
          </div>
        ) : (
          // Collapsed: the day cards scroll within the column. Below `lg` the
          // container itself is pulled to the screen edges (cancelling the page
          // Shell's `px-4`) and re-insets its content with `px-4` — so the
          // Options divider can bleed full width with `-mx-4` instead of being
          // clipped back to the padding box, and the scrollbar sits at the page
          // edge rather than hugging the cards. Desktop keeps the rail gutter.
          <div
            className="min-h-0 min-w-0 flex-1 overflow-y-auto pb-6 max-lg:-mx-4 max-lg:overflow-x-hidden max-lg:px-4 lg:pr-1"
            onScroll={(e) => onScrolledChange?.(e.currentTarget.scrollTop > 4)}>
            {/* 8px between cards on mobile against their own 12px padding, so
                "inside a card" and "between cards" read as different levels
                (12px for both made the list flat). Desktop keeps 12px. */}
            <div ref={dayListRef} className="flex flex-col gap-2 lg:gap-3">
              {/* A read-only surface can't order, so a "days left to order"
                  countdown (or an ordering-paused notice) would only mislead. */}
              {!lookahead && !readOnly ? (
                <WeekOrderingBanner
                  deadline={weekDeadline}
                  menusResolved={hasMenuByDay.every((v) => v !== null)}
                  anyMenus={hasMenuByDay.some((v) => v === true)}
                  paused={orderingPaused}
                  productionRecordsHref={`/production?locationId=${encodeURIComponent(school.id)}`}
                />
              ) : null}
              {weekKeys.map((dateKey, i) => (
                <DayCard
                  key={dateKey}
                  school={school}
                  dateKey={dateKey}
                  alreadyOrdered={collapsedAlreadyOrdered}
                  readOnly={readOnly || lookahead}
                  lookahead={lookahead}
                  parentFieldTrip={parentFieldTripByDay.get(dateKey) ?? null}
                  serviceDetailsHref={lookahead ? undefined : serviceHref(dateKey)}
                  serviceComplete={serviceCompleteByDay.get(dateKey) === true}
                  noService={noServiceDates.has(dateKey)}
                  cta={dayCta[i] ?? 'details'}
                  ctaPrimary={i === primaryIndex}
                  open={false}
                  // Lookahead expands in place on every breakpoint — the
                  // mobile day-page route belongs to the school surface.
                  // History-mode days navigate to the service page instead.
                  href={
                    isMobile && !lookahead
                      ? dayCta[i] === 'service-details'
                        ? serviceHref(dateKey)
                        : dayHref(dateKey)
                      : undefined
                  }
                  onOpenChange={(o) => {
                    if (o) {
                      setExpandedDate(dateKey)
                    }
                  }}
                />
              ))}
            </div>
            {/* Mobile-only quick actions, below the week. Deliberately a SIBLING
                of `dayListRef` — that element's height pins the desktop rail,
                and Options must not inflate the measurement. */}
            {isMobile && !lookahead ? (
              <section className="-mx-4 mt-6 border-t px-4 pt-6">
                <h2 className="text-base font-semibold text-foreground">Options</h2>
                {/* `pl-3` matches the day card's own `p-3`, so the option icons
                    share a left edge with the cards' date pills. The heading
                    stays on the gutter, where the card borders are. */}
                <div className="mt-2 flex flex-col pl-3">
                  <SchoolOrderActions school={school} schools={schools} variant="list" readOnly={readOnly} />
                </div>
              </section>
            ) : null}
          </div>
        )}
        <div className="hidden w-[240px] shrink-0 flex-col pb-6 lg:flex lg:min-h-0">
          <RightRail
            school={school}
            schools={schools}
            weekKeys={weekKeys}
            orderedDates={orderedDates}
            noServiceDates={noServiceDates}
            onSelectWeek={onAnchorChange}
            height={dayListHeight}
            hideCart={hideCart}
            hideDeadline={readOnly || lookahead}
          />
        </div>
      </div>
      {hideCart ? null : <MobileCartBar school={school} schools={schools} />}
    </div>
  )
}

/**
 * Week-level ordering state, full width of the day list (Sheila, 2026-08-24:
 * banner style, evenly spaced, matching the cards beneath). Three states:
 * open (light blue, days-left countdown), closed (light orange), and
 * not-yet-available (grey — the week's menus aren't published, so there is
 * nothing to order against). Hidden while the menu queries are unresolved
 * (no state flashing) and when a school has no deadline policy AND menus
 * exist (the countdown would be a lie).
 */
function WeekOrderingBanner({
  deadline,
  menusResolved,
  anyMenus,
  paused,
  productionRecordsHref,
}: {
  deadline: DeadlineStatus
  menusResolved: boolean
  anyMenus: boolean
  paused: boolean
  productionRecordsHref: string
}): ReactElement | null {
  // The pause outranks every other week state: no countdown may promise
  // orderability while the submit gate would reject the order.
  if (paused) {
    return <OrderingPausedBanner productionRecordsHref={productionRecordsHref} />
  }
  if (!menusResolved) {
    return null
  }
  const state: 'unavailable' | 'closed' | 'open' | null =
    !anyMenus && !deadline.closed
      ? 'unavailable'
      : deadline.daysRemaining === null
        ? null
        : deadline.closed
          ? 'closed'
          : 'open'
  if (state === null) {
    return null
  }
  const styles = {
    open: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
    closed: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
    unavailable: 'bg-muted text-muted-foreground',
  }[state]
  return (
    <div className={cn('flex w-full items-center gap-2 rounded-xl px-4 py-3 text-sm', styles)}>
      <Clock className="h-4 w-4 shrink-0" strokeWidth={2} />
      {state === 'open' ? (
        <span>
          <span className="font-semibold">
            {deadline.daysRemaining} {deadline.daysRemaining === 1 ? 'day' : 'days'}
          </span>{' '}
          left to order
        </span>
      ) : state === 'closed' ? (
        <span className="font-medium">Ordering closed for this week</span>
      ) : (
        <span className="font-medium">Ordering not available yet</span>
      )}
    </div>
  )
}
